import { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import UserBehaviorLogger from '../../../../../../../shared/UserBehaviorLogger';
import { IPageDataState } from '../../../store/pageData';
import { ViewableElementContext } from './ViewableElementWrapper';

interface Props {
  id: string;
  position: 'top' | 'middle' | 'bottom';
  dataClParams: string;
  enableLocalLogging?: boolean;
  localLoggingData?: {
    pageData: IPageDataState;
    clVmodule?: string;
    clLink?: string;
  };
}

const ViewableElementBody = styled.div<{ $position: Props['position'] }>`
  /* viewable測定用 共通 */
  position: absolute;
  left: 1px; /* 要素が画面端にくっついているとviewable判定にならないので、左側を1px空ける */
  /* viewable測定用 位置ごと */
  ${({ $position }) => {
    switch ($position) {
      case 'top':
        return css`
          top: 0;
        `;
      case 'middle':
        return css`
          top: 50%; /* 広告viewable impressionの定義に合わせる */
        `;
      case 'bottom':
        return css`
          bottom: 0;
        `;
      default:
        return;
    }
  }}
`;

const Link = styled.a`
  display: inline-block;
  width: 0;
  height: 0;
`;

/**
 * このComponentの親要素は計測範囲基準となるElementで、必ずViewableElementWrapperと併用すること
 *
 * @param props
 */
const ViewableElement: React.FC<Props> = props => {
  const context = useContext(ViewableElementContext);
  const [onMount, setOnMount] = useState(false);
  const viewableElement = useRef<HTMLDivElement>(null);
  const currentElement = viewableElement.current;

  useEffect(() => {
    setOnMount(true);
  }, []);

  /**
   * 要素がCSRされたらローカルにログを落とす処理を呼び出す。
   */
  useEffect(() => {
    localLogging(currentElement);
  }, [currentElement]);

  useEffect(() => {
    if (window.customLogger) {
      // Mountされてない時にビーコンを飛ばそうとするとCustomLogがWarnログを吐くため、Mount後にビーコンを飛ばす
      if (onMount && !window.customLogger.isTargetModule(props.id)) {
        window.customLogger.addModulesWithViewability(props.id, false, {
          logViewWithoutLinks: false
        });
      }
    }
  });

  // viewableしたらlocal内にviewableログを落とす
  const localLogging = (target: HTMLDivElement | null) => {
    // local内にviewableログを落とさない場合は何も処理を行わない
    if (!props.enableLocalLogging) {
      return;
    }
    // オプション
    const observerOption = {
      // ルートとして指定するDOM（無ければviewport）に擬似マージンの様な物を持たせる(top, right, bottom, left) 例)500px 0px 0px 0px
      // 例えば500px上で発火したい場合は500px 0px 0px 0pxとしてthresholdを0にする
      rootMargin: '0px',
      // rootに指定した要素が何割viewInしているか
      // 0~1で指定。0だと1pxでも見えた場合に発火1だと要素全体が見えた場合に発火
      threshold: 0.25
    };

    // viewable Elementがrenderされた後にobserverを設定
    const observer = new IntersectionObserver(entries => {
      // インスタンス作成と同時にコールバックが呼び出されるのが仕様らしいが、
      // 実際には要素は画面に入ってないので以下の方法で回避する。
      // - https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
      // - https://stackoverflow.com/questions/53214116/intersectionobserver-callback-firing-immediately-on-page-load
      if (!entries![0]?.isIntersecting) {
        return;
      }

      if (props.localLoggingData?.clVmodule) {
        UserBehaviorLogger.logViewEvent(
          props.localLoggingData.pageData,
          props.localLoggingData.clVmodule,
          props.localLoggingData.clLink
        );
      }
      if (target) {
        // 一度viewログを落としたら監視を解除する。
        observer.unobserve(target);
      }
    }, observerOption);
    if (target) {
      observer.observe(target);
    }
  };

  // viewable ElementはSSRしない
  if (!onMount) {
    return null;
  }

  // 親要素がない場合はコンソールにエラーを出す
  if (!context) {
    // eslint-disable-next-line no-console
    console.error(`ViewableElement must be used inside a ViewableElementWrapper. ${props.id}`);
  }

  return (
    <ViewableElementBody id={props.id} $position={props.position} ref={viewableElement}>
      <Link data-cl-params={props.dataClParams} />
    </ViewableElementBody>
  );
};

export default ViewableElement;
